import { useEffect, useRef } from 'react';
import { EventSourcePolyfill } from 'event-source-polyfill';
import { useAppSelector } from 'store/hook';
import DispatchMonitorAPI from 'api/dispatch-monitor.api';

export interface AssetLiveStatusUpdate {
  status: string;
  reason: string;
  description: string;
}

/**
 * Opens its own SSE connection to dispatch-monitor/live-dispatch-sse and
 * invokes `onStatusUpdate` with a device's live operational status
 * (STANDBY/DOWN/ACTIVE/etc.) whenever a STATUS action arrives on the
 * `xtab:{customerCode}:last:pubsub` channel.
 *
 * Sibling to useOperatorLiveStatus (same channel, independent connection) —
 * kept separate so callers that only care about one concern don't have to
 * filter the other's actions out of a shared handler.
 */
export function useAssetLiveStatus(
  onStatusUpdate: (device: string, update: AssetLiveStatusUpdate) => void,
  enabled = true
) {
  const accessToken = useAppSelector((s) => s.authReducer.accessToken);
  const customerId = useAppSelector((s) => s.persistedReducer.customer.id);
  const customerCode = useAppSelector((s) => s.persistedReducer.customer.code);
  const dispatchMonitorApi = useRef(new DispatchMonitorAPI()).current;
  const esRef = useRef<InstanceType<typeof EventSourcePolyfill> | null>(null);
  const onStatusUpdateRef = useRef(onStatusUpdate);
  onStatusUpdateRef.current = onStatusUpdate;

  useEffect(() => {
    if (!enabled || !accessToken || !customerId || !customerCode) return;

    const url = dispatchMonitorApi.liveDispatchSSEUrl(customerId);
    const es = new EventSourcePolyfill(url, {
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    esRef.current = es;

    es.onmessage = function (e: any) {
      try {
        const outer = JSON.parse(e.data as string) as {
          channel?: string;
          message?: string | Record<string, any>;
          sse_heartbeat?: boolean;
        };

        if (outer?.sse_heartbeat) return;
        if (outer?.channel !== `xtab:${customerCode}:last:pubsub`) return;
        if (outer?.message === undefined) return;

        const msg = (
          typeof outer.message === 'string'
            ? JSON.parse(outer.message)
            : outer.message
        ) as Record<string, any>;

        const device = msg?.device as string | undefined;
        const status = msg?.payload?.status as string | undefined;
        if (!device || !status) return;

        onStatusUpdateRef.current(device, {
          status,
          reason: (msg?.payload?.reason as string | undefined) ?? '',
          description: (msg?.payload?.description as string | undefined) ?? '',
        });
      } catch (err) {
        console.error('[asset-live-status] parse error:', err);
      }
    };

    es.onerror = () => {
      // EventSourcePolyfill auto-reconnects — no action needed
    };

    return () => {
      es.close();
      esRef.current = null;
    };
  }, [enabled, accessToken, customerId, customerCode, dispatchMonitorApi]);
}

export default useAssetLiveStatus;
